// ---------------------------------------------------------------------------
// Placeholder card for the always-on "Google Maps" route slot.
// ---------------------------------------------------------------------------
// The product contract is: one of the surfaced routes is ALWAYS the
// literal Google Maps route — so the user can compare every Pleasant
// alternative against the route Google would give them.
//
// When the Google Directions fetch is in flight we render a skeleton in
// the slot; when it fails (quota / network / no API key) we render an
// inline error with a Retry button. The slot itself is never empty —
// hiding it would silently break the comparison promise.
//
// Visually this matches RouteCard's resting (unselected) shell so the
// row of cards stays the same height + rhythm regardless of state.
// ---------------------------------------------------------------------------

import { Map as MapIcon, RefreshCw, AlertTriangle } from "lucide-react";

interface Props {
  state: "loading" | "error";
  onRetry?: () => void;
}

export const GoogleRouteSlot = ({ state, onRetry }: Props) => {
  const isError = state === "error";
  return (
    <div
      role="group"
      aria-label={
        isError
          ? "Fastest route — failed to load"
          : "Fastest route — loading"
      }
      className="rounded-2xl border bg-card p-4 shadow-sm border-border min-h-[140px] flex flex-col gap-3"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <MapIcon className="w-4 h-4 text-route-fastest" aria-hidden />
        <span>Fastest route</span>
        <span className="text-xs text-muted-foreground font-normal">
          Comparison baseline
        </span>
      </div>

      {state === "loading" ? (
        <div className="space-y-2 animate-pulse" aria-hidden>
          <div className="h-3 rounded bg-muted w-1/2" />
          <div className="h-3 rounded bg-muted w-2/3" />
          <div className="h-3 rounded bg-muted w-1/3" />
          <span className="sr-only">Loading the fastest route…</span>
        </div>
      ) : (
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle
            className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <div className="flex-1 min-w-0">
            <p className="text-foreground">Couldn't load the fastest route.</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Network or quota issue. Try again in a moment.
            </p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:ring-[3px] focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded px-1 -mx-1"
              >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                Retry
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
